import type { RefObject } from "react";
import { useMemo, useEffect, useCallback, useState } from "react";
import { isVideoLegacySeason } from "~/components/blocks/product/type-guard";
import { getVideoRatingForDetail } from "~/components/blocks/product/util/get-video-rating-for-detail";
import { useMatchMedia } from "~/hooks/use-match-media";
import type { G_GetVideoQuery } from "~/lib/graphql";
import { isEmptyObject } from "~/util/is-empty-object";

type Args = {
  videoData: G_GetVideoQuery["video"];
  ref: RefObject<HTMLDivElement>;
};

export const useProductInfo = ({ videoData, ref }: Args) => {
  const { isSpSize } = useMatchMedia();

  const [isShowRemainingItems, setIsShowRemainingItems] = useState(false);

  const [scrollHeight, setScrollHeight] = useState(0);

  const isOverTwentyInfo = useMemo(
    () => 20 < videoData.casts.length + videoData.staffs.length + videoData.genres.length,
    []
  );

  const hasCastData = 0 < videoData.casts.length;

  const hasNotCastData =
    0 < videoData.staffs.length ||
    0 < videoData.genres.length ||
    videoData.copyright !== "" ||
    (isVideoLegacySeason(videoData) && !isEmptyObject(videoData.series ?? {}));

  const { isGravure } = getVideoRatingForDetail(videoData.rating.category);

  const onToggleShowRemainingItems = useCallback(() => setIsShowRemainingItems((prev) => !prev), []);

  useEffect(() => {
    const scope = ref.current;
    if (scope === null || !isOverTwentyInfo) return;
    setScrollHeight(scope.scrollHeight);
  }, [isSpSize, isOverTwentyInfo, isShowRemainingItems]);

  // memo: 画面リサイズを監視する処理, spからtabletにブレイクした時にもっと見るを戻す、その上で一つ目のuseEffectで再計算させる
  useEffect(() => {
    if (!isOverTwentyInfo || !isShowRemainingItems) return;
    setIsShowRemainingItems(false);
  }, [isSpSize]);

  return {
    /** 21件以上の情報表示判定フラグ */
    isShowRemainingItems,
    /** 21件以上データがあるときの表示領域の高さ */
    scrollHeight,
    /** データ数が20件を超えているかの判定フラグ */
    isOverTwentyInfo,
    /** 出演者の情報が1つでも存在するかどうかをチェックするための判定式 */
    hasCastData,
    /** 出演者以外の情報が1つでも存在するかどうかをチェックするための判定式 */
    hasNotCastData,
    /** グラビア作品判定フラグ*/
    isGravure,
    /** もっと見るのtoggleイベントハンドラ */
    onToggleShowRemainingItems,
  };
};
